import { Component, OnInit } from '@angular/core';
import {
    IQuotationBlock,
    IQuotationCost,
    IQuotationSection,
    IQuotationsQuotation
} from "libraries/service/resources/quotation-quotations/IQuotationsQuotation";
import { ActivatedRoute } from "@angular/router";
import { tap, pluck, map, finalize, flatMap } from "rxjs/operators";
import { Yii } from "libraries/interfaces/Yii";
import { NotificationType } from "libraries/interfaces/INotification";
import { INotification } from "../cost/cost.component";
import { LabelService } from "libraries/service/label/label.service";
import { AppRootService } from "../../../app/shared/services/app-root/app-root-service";
import { InstanceService } from "libraries/service/instance/instance.service";
import { QuotationBreadcrumbService } from "app_quotation/shared/components/breadcrumb/quotation-breadcrumb.service";
import { IBreadcrumbItem } from "libraries/service/breadcrumb/IBreadcrumbItem";
import { CostAppConfigService } from '../cost/services/cost-app-config/cost-app-config.service';
import { AppHeaderTitleQuotationService } from 'app_quotation/shared/services/app-header-title-quotation/app-header-title-quotation.service';
import { CachedQuotationService } from 'app_quotation/shared/services/cached-quotation.service';
import { ErrorTracingService, TraceSeverity } from 'libraries/service/error-tracing/error-tracing.service';
import { ITab, TabCode } from 'app/shared/model/ITab';
import { QuotationQuotationsResourceService } from 'libraries/service/resources/quotation-quotations/quotation-quotations-resource.service';
import { EHosts } from "../../../libraries/enums/EHosts";
import { IQuotationResolved } from 'app_quotation/shared/services/quotation-resolve/quotation.resolve';
import { IXRateHandler, XRateService } from 'app_quotation/shared/services/xRate/xRate.service';
import { WatabiUrlService } from '../cost/services/watabi-url/watabi-url.service';
import { forkJoin, Observable } from "rxjs";
import { ILabels } from "../../../libraries/interfaces/ILabels";
import { BtnStyleTypes } from "../../../libraries/ui_button/btn.component";
import { EQuotationEntryPoints } from "libraries/enums/e-quotation-entry-points";

@Component({
    selector: 'retail',
    template: `
        <grid-tab-navigation *ngIf="tabs.length !== 0"
                             [tabs]="tabs"
                             [deactivationReason]="deactivationReason"
                             [canValidate]="quotation.externalReference.detail && quotation.externalReference.detail.quotation"
                             (onClose)="redirect()"
                             (onValidate)="propagateThenRedirect()"></grid-tab-navigation>

        <div class="app-wrapper app-quotation">
            <loader *ngIf="loading.active; else loadedContainer" [message]="loading.message"></loader>
            <ng-template #loadedContainer>
                <exchange-rate-information [quotation]="quotationForExchangeRate"
                                           (refreshExchangeRateEvent)="refreshXRates()">
                </exchange-rate-information>
                <!--<rate-invalidity-notification *ngIf="quotation"
                                              [isXRateValid]="quotation?.isXRateValid"
                                              [hasFoundValidRate]="hasFoundRate"
                                              (onRefreshRates)="refreshXRates()">
                </rate-invalidity-notification>-->
                <notification *ngIf="errorNotification.active"
                              [type]="notificationType.Error">
                    {{errorNotification.message}}
                    <ng-container *ngIf="showRefreshButton">
                        <btn [value]="wording.refresh" (onClick)="refresh()" [type]="refreshButtonType"></btn>
                    </ng-container>
                </notification>
                <cost-warning *ngIf="quotation?.warnings"
                              [warningsList]="quotation.warnings">
                </cost-warning>
                <retail-grid [quotation]="quotation"
                             (isXRateNoValid)="isXRateNoValid(false)"
                             (quotationChange)="quotationForExchangeRate=$event"
                             (onError)="showError($event)"
                             *ngIf="quotation">
                </retail-grid>
            </ng-template>
        </div>
    `,
    styles: [`
        .app-quotation {
            width: auto;
            max-width: unset;
            margin: .75rem;
        }
    `]
})

export class RetailComponent implements OnInit, IXRateHandler {
    public errorNotification: INotification = {
        active: false,
        message: Yii.t('wui-quotation-js', 'Erreur lors du chargement de la Cotation')
    };
    public showRefreshButton: boolean;
    public wording: ILabels = {
        refresh : Yii.t('wui-shared-js', 'Rafraîchir la page')
    };
    public refreshButtonType: BtnStyleTypes = BtnStyleTypes.actionLink;


    public notificationType: typeof NotificationType = NotificationType;

    public loading: INotification = {
        active: false,
        message: Yii.t('wui-quotation-js', 'Chargement de la Cotation en cours'),
    };

    public quotation: IQuotationsQuotation;
    public quotationForExchangeRate: IQuotationsQuotation;
    public showCloseButton: boolean = false;
    public breadcrumbItems: IBreadcrumbItem[];
    public hasFoundRate: boolean = true;
    public tabs: ITab[] = [];
    public deactivationReason: string = Yii.t('wui-quotation-js', 'Aucun bloc en option trouvé');

    constructor(
        private readonly activatedRoute: ActivatedRoute,
        private readonly cachedQuotationService: CachedQuotationService,
        private readonly labelService: LabelService,
        private readonly appRootService: AppRootService,
        private readonly instanceService: InstanceService,
        private readonly quotationBreadcrumbService: QuotationBreadcrumbService,
        private readonly errorTracingService: ErrorTracingService,
        private readonly costAppConfigService: CostAppConfigService,
        private readonly appHeaderTitleQuotationService: AppHeaderTitleQuotationService,
        private readonly quotationQuotationsResourceService: QuotationQuotationsResourceService,
        private readonly xRateService: XRateService,
        private readonly watabiUrlService: WatabiUrlService,
    ) {
    }

    public ngOnInit(): void {
        this.breadcrumbItems = this.quotationBreadcrumbService.activateItem(1);

        this.activatedRoute.data.pipe(
            pluck('quotation'),
            tap(() => {
                this.cachedQuotationService.get(+this.activatedRoute.snapshot.paramMap.get('id'));
            }),
            finalize(() => {
                this.loading.active = false;
            }),
        ).subscribe({
            next: ({hasOptions, quotation}: IQuotationResolved): void => {
                this.initQuotation(quotation);

                const parentTab: ITab = this.appRootService.currentAppConfig.tabs.find((tab: ITab) => tab.code === EQuotationEntryPoints.retail);

                if (parentTab) {
                    this.tabs = parentTab.tabs.map((tab: ITab) => {
                        if (!hasOptions && tab.code === TabCode.option) {
                            return {
                                ...tab,
                                disabled: true
                            };
                        }

                        return tab;
                    });
                }
            },
            error: (error: Error): void => {
                //manage Error
                this.loading.active = false;
                this.errorNotification = {
                    active: true,
                    message: this.labelService.completeLabel(
                        Yii.t('wui-quotation-js', 'Erreur lors du chargement de la Cotation : %placeholder%'),
                        error.message,
                    ),
                };
                this.errorTracingService.captureMessage(error.message, TraceSeverity.Warning);
                // Otherwise, it's not possible to see the stack trace
                console.warn(error);
            }
        });
    }

    public initQuotation(quotation: IQuotationsQuotation): void {
        this.quotation = quotation;
        this.quotationForExchangeRate = quotation;

        this.appHeaderTitleQuotationService.changeTitle(quotation.id);

        if (quotation.externalReference
            && quotation.externalReference.host
            && quotation.externalReference.host.toLowerCase() === EHosts.WATABI.toLowerCase()) {
            this.showCloseButton = true;
        }

        this.costAppConfigService.prepareConfig(quotation);

        if (this.quotation.title) {
            this.appRootService.setAppConfig(
                {
                    ...this.appRootService.getAppConfig(this.appRootService.getCurrentAppFromLocation()),
                    secondaryName: this.quotation.title
                }
            );
            this.appRootService.setBrowserTitle();
        }
    }

    public isXRateNoValid(isXRateValid: boolean) {
        this.quotation.isXRateValid = isXRateValid;
    }

    public propagateThenRedirect(): void {
        this.loading = {
            active: true,
            message: Yii.t('wui-quotation-js', 'Mise à jour en cours...')
        };

        const quoteBlocks: IQuotationBlock[] = this.quotation.sections
            .reduce((blocks: IQuotationBlock[], section: IQuotationSection) => {
                const relatedQuoteBlocks: IQuotationBlock[] = (section.blocks || [])
                    .filter((block: IQuotationBlock) => Boolean(
                        block &&
                        block.externalReference &&
                        block.externalReference.host === EHosts.WATABI));
                return [...blocks, ...relatedQuoteBlocks];
            }, []);
        if (this.quotation.externalReference && this.quotation.externalReference.host === EHosts.WATABI && quoteBlocks.length) {
            this.updateWatabiBlocks(quoteBlocks);
        } else {
            this.propagateThenRedirectAction();
        }

    }

    public redirect(): void {
        window.location.href = this.watabiUrlService.generateFrontendQuoteUrl(this.quotation);
    }

    public refreshXRates(): void {
        this.xRateService.refreshXRatesAndComponentState(this);
    }

    public showError(errorMessage: string): void {
        this.showRefreshButton = true;
        this.errorNotification = {
            active: true,
            message: errorMessage,
        };
    }

    public refresh(): void {
        document.location.reload();
    }

    private propagateThenRedirectAction(): void {
        this.quotationQuotationsResourceService.propagate(this.quotation.id).subscribe({
            next: () => {
                this.loading.message = Yii.t('wui-quotation-js', 'Redirection vers la cotation...');
                this.redirect();
            },
            error: (error: Error): void => {
                this.loading.active = false;

                this.errorNotification = {
                    active: true,
                    message: this.labelService.completeLabel(
                        Yii.t('wui-quotation-js', error.message),
                        error.message,
                    ),
                };
                this.errorTracingService.captureMessage(error.message, TraceSeverity.Error);
            }
        });
    }

    private updateWatabiBlocks(quoteBlocks: IQuotationBlock[]): void {
        const watabiBlocksGet: Array<Observable<object>> = quoteBlocks
            .map((block: IQuotationBlock) => this.quotationQuotationsResourceService.getBlock(block.externalReference.code));
        forkJoin(watabiBlocksGet)
            .pipe(
                flatMap((watabiBlocks: object[]) => {

                    const request: object = watabiBlocks.map((watabiBlock: object) => {
                        const relatedQuoteBlock: IQuotationBlock = quoteBlocks
                            // @ts-ignore
                            .find((quoteBlock: IQuotationBlock) => quoteBlock.externalReference.code === watabiBlock.idItem.toString());
                        return this.quotationQuotationsResourceService.generateRequest(watabiBlock, relatedQuoteBlock);
                    });
                    return this.quotationQuotationsResourceService.updateBlock(request);
                })
            )
            .subscribe(() => this.propagateThenRedirectAction());
    }
}
